import * as React from "react";
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Button,
} from "@/components/ui";

export interface CountryCode {
  code: string;
  dialCode: string;
  flag: string;
  name: string;
}

// FIX-22: COUNTRY_CODES is now a META LOOKUP (dial codes + flags), not the dropdown list.
// The login dropdown renders countries.login_enabled = true from the live DB (see
// useLoginCountries below). Index 0 (BD) remains the registration default — unchanged
// behaviour; the CBS registration-country defect is tracked separately (INV-C debt).
export const COUNTRY_CODES: CountryCode[] = [
  { code: "BD", dialCode: "+880", flag: "🇧🇩", name: "Bangladesh" },
  { code: "KH", dialCode: "+855", flag: "🇰🇭", name: "Cambodia" },
  { code: "LA", dialCode: "+856", flag: "🇱🇦", name: "Laos" },
  { code: "PH", dialCode: "+63", flag: "🇵🇭", name: "Philippines" },
  { code: "VN", dialCode: "+84", flag: "🇻🇳", name: "Vietnam" },
  { code: "MM", dialCode: "+95", flag: "🇲🇲", name: "Myanmar" },
  { code: "KE", dialCode: "+254", flag: "🇰🇪", name: "Kenya" },
  { code: "UG", dialCode: "+256", flag: "🇺🇬", name: "Uganda" },
  { code: "PK", dialCode: "+92", flag: "🇵🇰", name: "Pakistan" },
  { code: "LK", dialCode: "+94", flag: "🇱🇰", name: "Sri Lanka" },
  { code: "ET", dialCode: "+251", flag: "🇪🇹", name: "Ethiopia" },
  { code: "TH", dialCode: "+66", flag: "🇹🇭", name: "Thailand" },
  { code: "AM", dialCode: "+374", flag: "🇦🇲", name: "Armenia" },
  { code: "AZ", dialCode: "+994", flag: "🇦🇿", name: "Azerbaijan" },
  { code: "UZ", dialCode: "+998", flag: "🇺🇿", name: "Uzbekistan" },
];

// Fetch-failure fallback preserves today's static-list idiom: the three live login countries.
const FALLBACK_LOGIN_COUNTRIES: CountryCode[] = COUNTRY_CODES.filter((c) =>
  ["KH", "LA", "PH"].includes(c.code)
);

let cachedLoginCountries: CountryCode[] | null = null;

// FIX-22: live login countries (anon read; the policy exposes login_enabled rows).
export const useLoginCountries = (): CountryCode[] => {
  const [list, setList] = React.useState<CountryCode[]>(cachedLoginCountries ?? FALLBACK_LOGIN_COUNTRIES);
  React.useEffect(() => {
    if (cachedLoginCountries) return;
    let cancelled = false;
    supabase
      .from("countries")
      .select("code, name")
      .eq("login_enabled", true)
      .order("name")
      .then(({ data, error }) => {
        if (cancelled || error || !data || data.length === 0) return;
        const mapped = data
          .map((r) => COUNTRY_CODES.find((c) => c.code === r.code))
          .filter((c): c is CountryCode => !!c);
        if (mapped.length > 0) {
          cachedLoginCountries = mapped;
          setList(mapped);
        }
      });
    return () => { cancelled = true; };
  }, []);
  return list;
};

export interface CountryCodeSelectorProps {
  value: CountryCode;
  onChange: (country: CountryCode) => void;
  disabled?: boolean;
  className?: string;
}

export const CountryCodeSelector: React.FC<CountryCodeSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  className,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const loginCountries = useLoginCountries();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "h-14 px-3 justify-between gap-1 text-base font-medium",
            "border-input bg-background hover:bg-accent",
            className
          )}
        >
          <span className="text-xl">{value.flag}</span>
          <span className="text-muted-foreground">{value.dialCode}</span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command>
          <CommandInput placeholder={t('components.countrySelector.searchCountry')} className="h-12" />
          <CommandList>
            <CommandEmpty>{t('components.countrySelector.noCountryFound')}</CommandEmpty>
            <CommandGroup>
              {loginCountries.map((country) => (
                <CommandItem
                  key={country.code}
                  value={`${country.name} ${country.dialCode}`}
                  onSelect={() => {
                    onChange(country);
                    setOpen(false);
                  }}
                  className="h-12 text-base"
                >
                  <span className="text-xl mr-2">{country.flag}</span>
                  <span className="flex-1">{country.name}</span>
                  <span className="text-muted-foreground">{country.dialCode}</span>
                  <Check
                    className={cn(
                      "ml-2 h-4 w-4",
                      value.code === country.code ? "opacity-100" : "opacity-0"
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default CountryCodeSelector;
